import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Subscription } from 'rxjs';
import { ITaxConfig } from 'src/app/common/tax/services/tax-register-config.service';
import { TaxRegisterConfigAccessService } from 'src/app/common/tax/services/tax-register-config-access.service';
import { TaxService } from 'src/app/common/tax/tax.service';
import { TaxRegisterContextService } from 'src/app/common/tax/services/tax-register-context.service';

@Component({
  selector: 'order-fiscal-switch',
  templateUrl: './order-fiscal-switch.component.html',
  styleUrls: ['./order-fiscal-switch.component.scss'],
})
export class OrderFiscalSwitchComponent implements OnInit {
  @Input() storeId: number;

  orgName = '';
  registerLabel = '';
  registerAddress = '';
  taxRegisterConfigured = false;
  private _isFiscalCheck = true;
  get isFiscalCheck() {
    return this._isFiscalCheck && !!this._storeConfig;
  }
  fiscalOperationInProgress = false;
  private signerStateSub: Subscription;
  loading = false;
  @Input() type: 'button' | 'card' = 'card';
  notConfigured = false;
  private _storeConfig: ITaxConfig;

  constructor(
    private taxRegisterConfigService: TaxRegisterConfigAccessService,
    private taxRegisterContextService: TaxRegisterContextService,
    private taxService: TaxService,
  ) {}

  updateIsFiscal(value: boolean) {
    this._isFiscalCheck = value;
  }

  async ngOnInit() {
    const contextConfig = await this._getContextConfigByStore();
    if (contextConfig) {
      const fiscalNumber = contextConfig.taxRegister?.fiscalNumber;
      const isDisabled = fiscalNumber
        ? await this.taxRegisterContextService.isDisabled(fiscalNumber)
        : true;
      const isValid =
        !isDisabled &&
        !!contextConfig.taxRegister?.fiscalNumber &&
        !!contextConfig.cashier?.subjectKeyId;

      if (!isValid) {
        this.notConfigured = true;
        return;
      }

      this._storeConfig = contextConfig;
      this._fillRegisterInfo(contextConfig);
      this.taxRegisterConfigured = await this.taxService
        .isAgentSignerReady(contextConfig)
        .catch(() => false);
      this._subscribeSignerState();
      return;
    }

    this._storeConfig = await this.taxRegisterConfigService.getStoreConfig(
      this.storeId,
    );
    if (!this._storeConfig) {
      this.notConfigured = true;
      return;
    }

    this._fillRegisterInfo(this._storeConfig);
    this.taxService.isAgentSignerReady(this._storeConfig).then((res) => {
      if (res) {
        this.taxRegisterConfigured = true;
      }
    });
    this._subscribeSignerState();
  }

  toggleFiscalOperation() {
    this._isFiscalCheck = !this._isFiscalCheck;
  }

  private async _getContextConfigByStore(): Promise<ITaxConfig | null> {
    if (this.storeId == null) {
      return null;
    }

    try {
      const taxRegister =
        await this.taxRegisterContextService.getTaxRegisterByStoreId(
          this.storeId,
        );
      if (!taxRegister?.fiscalNumber) {
        return null;
      }

      return await this.taxRegisterContextService.getTaxConfig(
        taxRegister.fiscalNumber,
      );
    } catch {
      return null;
    }
  }

  private _fillRegisterInfo(config: ITaxConfig): void {
    this.orgName = config.orgFullName;
    this.registerLabel = `${config.taxRegister?.fiscalNumber} ${
      config.taxRegister?.name || ''
    }`.trim();
    this.registerAddress = config.taxRegister?.address || '';
  }

  private _subscribeSignerState(): void {
    this.signerStateSub?.unsubscribe();
    this.signerStateSub = this.taxService.signerState.subscribe((state) => {
      switch (state) {
        case 'off':
        case 'error':
          this.loading = false;
          this.taxRegisterConfigured = false;
          break;

        case 'pending':
        case 'keyReady':
          this.loading = true;
          break;

        case 'ready':
          this.loading = false;
          this.taxRegisterConfigured = true;
          break;

        default:
          this.loading = false;
          this.taxRegisterConfigured = false;
          break;
      }
    });
  }

  ngOnDestroy() {
    if (this.signerStateSub) {
      this.signerStateSub.unsubscribe();
    }
  }
}
